import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  CircularProgress,
  Drawer,
  Chip,
  Breadcrumbs,
  Button,
  Divider,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  Folder as FolderIcon,
  FileText,
  Filter as FilterIcon,
  Sparkles,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Download as DownloadIcon,
  FolderPlus,
  UploadCloud,
  Trash2,
  Folder as ArchiveLogoIcon
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import LoaderProgress from '../components/ui/loader-progress';

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

const StyledCard = styled(Card)({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

function DocumentManagerPage() {
  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [rootFolders, setRootFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    sort: 'name_asc'
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [selectedIds, setSelectedIds] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [pathsCollapsed, setPathsCollapsed] = useState(false);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderLoading, setNewFolderLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadPath('');
  }, []);

  useEffect(() => {
    const term = (filters.search || '').trim();
    if (!term) {
      setSearchResults(null);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }
    if (term.length < 2) {
      setSearchResults(null);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    setSearchError(null);
    api
      .get('/fs/search', {
        params: {
          path: currentPath,
          q: term
        }
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setSearchResults(Array.isArray(data.files) ? data.files : []);
      })
      .catch(() => {
        if (cancelled) return;
        setSearchError('Errore durante la ricerca nei sottopercorsi');
        setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) {
          setSearchLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filters.search, currentPath]);

  const loadPath = async (path) => {
    setLoading(true);
    setError(null);
    setSelectedIds([]);
    setSelectedFile(null);
    setPreviewUrl('');
    setPreviewOpen(false);
    try {
      const res = await api.get('/fs/list', {
        params: { path }
      });
      const data = res.data || {};
      setCurrentPath(data.path || path || '');
      setFolders(data.folders || []);
      setFiles(data.files || []);
      if (!path) {
        setRootFolders(data.folders || []);
      }
      setPage(0);
    } catch (err) {
      setError('Errore nel caricamento del contenuto');
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (patch) => {
    setFilters((prev) => ({
      ...prev,
      ...patch
    }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleSelectAll = (checked, visibleFiles) => {
    if (checked) {
      setSelectedIds(visibleFiles.map((f) => f.key));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (key) => {
    setSelectedIds((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const handleOpenFolder = (folderPath) => {
    loadPath(folderPath);
  };

  const handleBreadcrumbClick = (targetPath) => {
    if (targetPath === currentPath) return;
    loadPath(targetPath);
  };

  const handleSelectFile = async (file) => {
    setSelectedFile(file);
    setPreviewUrl('');
    setPreviewOpen(true);
    if (!file.previewable) {
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await api.post('/fs/presign-preview', { key: file.key });
      const data = res.data || {};
      setPreviewUrl(data.url || '');
    } catch (err) {
      toast.error('Impossibile caricare l\'anteprima');
      setPreviewUrl('');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleDownload = async () => {
    if (!selectedFile) return;
    setDownloadLoading(true);
    try {
      const res = await api.post('/fs/presign-download', { key: selectedFile.key });
      const data = res.data || {};
      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      toast.error('Impossibile scaricare il file');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;
    setAnalyzeLoading(true);
    try {
      await api.post('/fs/analyze', { key: selectedFile.key });
      toast.success('Analisi avviata');
      setAiPanelOpen(true);
    } catch (err) {
      toast.error('Impossibile avviare l\'analisi AI');
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const hasFiltersActive = useMemo(() => {
    return filters.search || filters.type !== 'all' || filters.sort !== 'name_asc';
  }, [filters]);

  const typeOptions = useMemo(() => {
    const set = new Set();
    files.forEach((f) => {
      if (f.extension) {
        set.add(f.extension.toLowerCase());
      }
    });
    return Array.from(set);
  }, [files]);

  const isDeepSearchActive = useMemo(() => {
    return Boolean((filters.search || '').trim() && searchResults);
  }, [filters.search, searchResults]);

  const effectiveFiles = isDeepSearchActive ? searchResults || [] : files;

  const entries = useMemo(() => {
    let folderEntries = isDeepSearchActive
      ? []
      : folders.map((folder) => ({
          type: 'folder',
          folder
        }));
    let fileEntries = effectiveFiles.map((file) => ({
      type: 'file',
      file
    }));

    if (filters.search && !isDeepSearchActive) {
      const q = filters.search.toLowerCase();
      folderEntries = folderEntries.filter(
        (e) => e.folder.name && e.folder.name.toLowerCase().includes(q)
      );
      fileEntries = fileEntries.filter(
        (e) => e.file.name && e.file.name.toLowerCase().includes(q)
      );
    }

    if (filters.type !== 'all') {
      fileEntries = fileEntries.filter((e) => {
        const ext = (e.file.extension || '').toLowerCase();
        return ext === filters.type;
      });
    }

    folderEntries.sort((a, b) => {
      const an = (a.folder.name || '').toLowerCase();
      const bn = (b.folder.name || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });

    fileEntries.sort((a, b) => {
      const isAsc = filters.sort.endsWith('_asc');
      if (filters.sort.startsWith('name')) {
        const an = (a.file.name || '').toLowerCase();
        const bn = (b.file.name || '').toLowerCase();
        if (an < bn) return isAsc ? -1 : 1;
        if (an > bn) return isAsc ? 1 : -1;
        return 0;
      }
      if (filters.sort.startsWith('date')) {
        const ad = a.file.lastModified ? new Date(a.file.lastModified).getTime() : 0;
        const bd = b.file.lastModified ? new Date(b.file.lastModified).getTime() : 0;
        return isAsc ? ad - bd : bd - ad;
      }
      if (filters.sort.startsWith('size')) {
        const as = Number(a.file.size || 0);
        const bs = Number(b.file.size || 0);
        return isAsc ? as - bs : bs - as;
      }
      return 0;
    });

    return [...folderEntries, ...fileEntries];
  }, [folders, effectiveFiles, filters, isDeepSearchActive]);

  const paginatedEntries = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return entries.slice(start, end);
  }, [entries, page, rowsPerPage]);

  const selectedCount = selectedIds.length;

  const pathSegments = useMemo(() => {
    if (!currentPath) return [];
    return currentPath.split('/').filter(Boolean);
  }, [currentPath]);

  const handleOpenNewFolderDialog = () => {
    setNewFolderName('');
    setNewFolderDialogOpen(true);
  };

  const handleConfirmNewFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      toast.error('Inserisci un nome per la cartella');
      return;
    }
    setNewFolderLoading(true);
    try {
      await api.post('/fs/create-folder', {
        path: currentPath,
        name
      });
      toast.success('Cartella creata');
      setNewFolderDialogOpen(false);
      setNewFolderName('');
      loadPath(currentPath);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore nella creazione della cartella');
    } finally {
      setNewFolderLoading(false);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const performUpload = async (filesToUpload) => {
    if (!filesToUpload.length) return;
    setUploading(true);
    try {
      const maxPerRequest = 50;
      for (let i = 0; i < filesToUpload.length; i += maxPerRequest) {
        const chunk = filesToUpload.slice(i, i + maxPerRequest);
        const formData = new FormData();
        formData.append('path', currentPath);
        chunk.forEach((file) => {
          formData.append('files', file);
        });
        await api.post('/fs/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success('Upload completato');
      loadPath(currentPath);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore durante l\'upload dei documenti');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const processIncomingFiles = async (filesList) => {
    const filesToUpload = Array.from(filesList || []);
    if (!filesToUpload.length) return;

    const overLimit = filesToUpload.filter(
      (file) => typeof file.size === 'number' && file.size > MAX_UPLOAD_BYTES
    );
    const validFiles = overLimit.length
      ? filesToUpload.filter((file) => !overLimit.includes(file))
      : filesToUpload;

    if (overLimit.length) {
      toast.error(
        `Sono stati esclusi ${overLimit.length} file perché superano il limite di 20MB per file.`
      );
    }

    if (!validFiles.length) {
      return;
    }

    const existingNames = new Set(
      files.map((f) => (f.name || '').toLowerCase())
    );
    const newOnes = [];
    const duplicates = [];
    validFiles.forEach((file) => {
      const name = (file.name || '').toLowerCase();
      if (existingNames.has(name)) {
        duplicates.push(file);
      } else {
        newOnes.push(file);
      }
    });
    if (!duplicates.length) {
      await performUpload(validFiles);
      return;
    }
    const confirmOverwrite = window.confirm(
      `Sono stati trovati ${duplicates.length} file con lo stesso nome nel percorso corrente.\n` +
      `OK: sovrascrivi i file esistenti.\nAnnulla: carica solo i file nuovi.`
    );
    if (confirmOverwrite) {
      await performUpload(filesToUpload);
    } else if (newOnes.length) {
      await performUpload(newOnes);
    } else {
      toast.info('Nessun file caricato: tutti i file selezionati esistono già in questo percorso.');
    }
  };

  const handleUploadChange = async (event) => {
    await processIncomingFiles(event.target.files);
  };

  const handleDropFiles = async (droppedFiles) => {
    await processIncomingFiles(droppedFiles);
  };

  const handleDeleteFolder = (folder) => {
    if (!folder || !folder.path) return;
    setDeleteTarget({ type: 'folder', item: folder });
    setDeleteDialogOpen(true);
  };

  const handleDeleteFile = (file) => {
    if (!file || !file.key) return;
    setDeleteTarget({ type: 'file', item: file });
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === 'folder') {
        await api.delete('/fs/folder', { data: { path: deleteTarget.item.path } });
        toast.success('Cartella eliminata correttamente');
      } else if (deleteTarget.type === 'file') {
        await api.delete('/fs/file', { data: { key: deleteTarget.item.key } });
        toast.success('File eliminato correttamente');
      }
      loadPath(currentPath);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      const defaultMessage =
        deleteTarget.type === 'folder'
          ? 'Errore durante l\'eliminazione della cartella'
          : 'Errore durante l\'eliminazione del file';
      toast.error(err?.response?.data?.message || defaultMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box
      sx={{
        pt: 1.5,
        pb: 3,
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ArchiveLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.25 }}>
              Documentale
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Archiviazione e gestione dei documenti dell&apos;organizzazione
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, minHeight: 0, flex: 1 }}>
        <Box
          sx={{
            width: pathsCollapsed ? 80 : 260,
            flexShrink: 0,
            transition: 'width 0.2s ease'
          }}
        >
          <StyledCard>
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: pathsCollapsed ? 'center' : 'space-between',
                cursor: 'pointer'
              }}
              onClick={() => setPathsCollapsed((prev) => !prev)}
            >
              {!pathsCollapsed && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Percorsi
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {pathsCollapsed && (
                  <Typography variant="caption" color="text.secondary">
                    Percorsi
                  </Typography>
                )}
                {pathsCollapsed ? (
                  <ChevronRight size={16} />
                ) : (
                  <ChevronLeft size={16} />
                )}
              </Box>
            </Box>
            <Box sx={{ p: 1, maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
              {loading && !currentPath && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  <LoaderProgress size={40} />
                </Box>
              )}
              {!loading && rootFolders.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Nessuna cartella root disponibile.
                </Typography>
              )}
              {rootFolders.map((folder) => {
                const isActive =
                  currentPath === folder.path || currentPath.startsWith(folder.path + '/');
                return (
                  <Box
                    key={folder.path}
                    onClick={() => handleOpenFolder(folder.path)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 0.75,
                      cursor: 'pointer',
                      borderRadius: 1.5,
                      gap: 1,
                      bgcolor: isActive ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <FolderIcon size={16} />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        fontWeight: isActive ? 600 : 400,
                        display: pathsCollapsed ? 'none' : 'block'
                      }}
                      noWrap
                    >
                      {folder.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </StyledCard>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StyledCard>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(145, 158, 171, 0.24)' }}>
              <DocumentBreadcrumbs
                currentPath={currentPath}
                segments={pathSegments}
                onClick={handleBreadcrumbClick}
              />
            </Box>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(145, 158, 171, 0.24)' }}>
              <DocumentToolbar
                filters={filters}
                onFiltersChange={handleFiltersChange}
                typeOptions={typeOptions}
                onRefresh={() => loadPath(currentPath)}
                selectedCount={selectedCount}
                onOpenAiPanel={() => setAiPanelOpen(true)}
                hasFiltersActive={hasFiltersActive}
                onCreateFolder={handleOpenNewFolderDialog}
                onUploadDocuments={handleUploadClick}
              />
              <Box sx={{ mt: 1 }}>
                <UploadDropzone
                  disabled={uploading}
                  onBrowse={handleUploadClick}
                  onDropFiles={handleDropFiles}
                />
              </Box>
            </Box>
            <Box sx={{ p: 0 }}>
              {isDeepSearchActive && (
                <Box sx={{ px: 2, pt: 2, pb: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    Risultati della ricerca in "{currentPath || 'root'}" e sottocartelle
                  </Typography>
                </Box>
              )}
              <DocumentList
                loading={loading || searchLoading}
                error={error || searchError}
                entries={paginatedEntries}
                totalEntries={entries.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                onOpenFolder={handleOpenFolder}
                onOpenFile={handleSelectFile}
                onDeleteFolder={handleDeleteFolder}
                onDeleteFile={handleDeleteFile}
                isDeepSearchActive={isDeepSearchActive}
                currentPath={currentPath}
              />
            </Box>
          </StyledCard>
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleUploadChange}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {deleteTarget?.type === 'folder' ? 'Elimina cartella' : 'Elimina file'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2">
            {deleteTarget?.type === 'folder'
              ? `Confermi l'eliminazione definitiva della cartella "${deleteTarget?.item?.name}" e di tutto il suo contenuto?`
              : `Confermi l'eliminazione definitiva del file "${deleteTarget?.item?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disableElevation
            disabled={deleteLoading}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newFolderDialogOpen} onClose={() => !newFolderLoading && setNewFolderDialogOpen(false)}>
        <DialogTitle>Nuova cartella</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Nome cartella"
            fullWidth
            size="small"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            disabled={newFolderLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderDialogOpen(false)} disabled={newFolderLoading}>
            Annulla
          </Button>
          <Button
            onClick={handleConfirmNewFolder}
            variant="contained"
            disabled={newFolderLoading}
          >
            Crea
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={Boolean(selectedFile)}
        onClose={handleClosePreview}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 440 },
            maxWidth: '100vw',
            borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow:
              '0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.24)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
            >
              <Sparkles size={16} />
              Anteprima documento
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={<Sparkles size={14} />}
              onClick={handleAnalyze}
              disabled={!selectedFile || analyzeLoading}
            >
              AI
            </Button>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, p: 2 }}>
            <DocumentPreviewPanel
              file={selectedFile}
              previewUrl={previewUrl}
              loading={previewLoading}
              onClose={handleClosePreview}
              onDownload={handleDownload}
              downloadLoading={downloadLoading}
              onAnalyze={handleAnalyze}
              analyzeLoading={analyzeLoading}
            />
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="bottom"
        open={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        PaperProps={{ sx: { height: { xs: '60vh', md: 360 } } }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Sparkles size={16} />
              Analisi AI documenti
            </Typography>
            <Button size="small" variant="text" onClick={() => setAiPanelOpen(false)}>
              Chiudi
            </Button>
          </Box>
          <Divider />
          <Typography variant="body2" color="text.secondary">
            In questa area potrai integrare la chat o le azioni AI basate sui documenti selezionati.
          </Typography>
        </Box>
      </Drawer>
    </Box>
  );
}

function DocumentBreadcrumbs({ currentPath, segments, onClick }) {
  if (!segments || segments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        root
      </Typography>
    );
  }

  const items = [
    {
      label: 'root',
      path: ''
    },
    ...segments.map((seg, index) => {
      const path = segments.slice(0, index + 1).join('/');
      return { label: seg, path };
    })
  ];

  return (
    <Breadcrumbs aria-label="breadcrumb">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        if (isLast) {
          return (
            <Typography key={item.path || 'root'} color="text.primary" variant="body2">
              {item.label}
            </Typography>
          );
        }
        return (
          <Typography
            key={item.path || 'root'}
            variant="body2"
            sx={{ cursor: 'pointer' }}
            color="text.secondary"
            onClick={() => onClick(item.path)}
          >
            {item.label}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
}

function DocumentToolbar({
  filters,
  onFiltersChange,
  typeOptions,
  onRefresh,
  selectedCount,
  onOpenAiPanel,
  hasFiltersActive,
  onCreateFolder,
  onUploadDocuments
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      <TextField
        size="small"
        placeholder="Cerca file..."
        value={filters.search}
        onChange={(e) => onFiltersChange({ search: e.target.value })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} />
            </InputAdornment>
          ),
          sx: {
            borderRadius: 999
          }
        }}
        sx={{ minWidth: 260, flexGrow: 1, maxWidth: 420 }}
      />
      <FormControl
        size="small"
        sx={{
          minWidth: 160,
          '& .MuiOutlinedInput-root': { borderRadius: 999 }
        }}
      >
        <InputLabel>Tipo file</InputLabel>
        <Select
          label="Tipo file"
          value={filters.type}
          onChange={(e) => onFiltersChange({ type: e.target.value })}
        >
          <MenuItem value="all">Tutti</MenuItem>
          {typeOptions.map((t) => (
            <MenuItem key={t} value={t}>
              {t.toUpperCase()}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        size="small"
        sx={{
          minWidth: 180,
          '& .MuiOutlinedInput-root': { borderRadius: 999 }
        }}
      >
        <InputLabel>Ordina per</InputLabel>
        <Select
          label="Ordina per"
          value={filters.sort}
          onChange={(e) => onFiltersChange({ sort: e.target.value })}
        >
          <MenuItem value="name_asc">Nome A-Z</MenuItem>
          <MenuItem value="name_desc">Nome Z-A</MenuItem>
          <MenuItem value="date_desc">Più recenti</MenuItem>
          <MenuItem value="date_asc">Più vecchi</MenuItem>
          <MenuItem value="size_desc">Dimensione decrescente</MenuItem>
          <MenuItem value="size_asc">Dimensione crescente</MenuItem>
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', marginLeft: 'auto' }}>
        {hasFiltersActive && (
          <Chip
            size="small"
            icon={<FilterIcon size={14} />}
            label="Filtri attivi"
            color="primary"
            variant="outlined"
          />
        )}
        <Tooltip title="Nuova cartella">
          <span>
            <IconButton
              size="small"
              aria-label="Nuova cartella"
              onClick={onCreateFolder}
            >
              <FolderPlus size={18} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Carica documenti">
          <span>
            <IconButton
              size="small"
              aria-label="Carica documenti"
              onClick={onUploadDocuments}
            >
              <UploadCloud size={18} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Ricarica il contenuto della cartella corrente">
          <span>
            <IconButton
              size="small"
              aria-label="Aggiorna"
              onClick={onRefresh}
            >
              <RefreshCw size={18} />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<Sparkles size={16} />}
          disabled={selectedCount === 0}
          onClick={onOpenAiPanel}
        >
          Azione AI
        </Button>
      </Box>
    </Box>
  );
}

function UploadDropzone({ onBrowse, onDropFiles, disabled }) {
  const [dragActive, setDragActive] = useState(false);

  const handleDragOver = (event) => {
    event.preventDefault();
    if (disabled) return;
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (disabled) return;
    const files = Array.from(event.dataTransfer.files || []);
    if (!files.length) return;
    onDropFiles(files);
  };

  return (
    <Box
      onClick={() => {
        if (!disabled) onBrowse();
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        borderRadius: 2,
        border: '1px dashed rgba(148, 163, 184, 0.7)',
        bgcolor: dragActive ? 'rgba(148, 163, 184, 0.08)' : 'rgba(15, 23, 42, 0.01)',
        px: 1.5,
        py: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      <UploadCloud size={16} />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="caption" color="text.secondary">
          Trascina qui i file oppure clicca per selezionare
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Limite: 20MB per file
        </Typography>
      </Box>
    </Box>
  );
}

function DocumentList({
  loading,
  error,
  entries,
  totalEntries,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onOpenFolder,
  onOpenFile,
  onDeleteFolder,
  onDeleteFile,
  isDeepSearchActive,
  currentPath
}) {
  const fileEntries = entries.filter((e) => e.type === 'file');
  const allChecked =
    fileEntries.length > 0 &&
    fileEntries.every((e) => selectedIds.includes(e.file.key));
  const indeterminate =
    selectedIds.length > 0 && !allChecked;

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <LoaderProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!loading && entries.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Nessun contenuto in questo percorso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Seleziona un altro percorso o verifica la configurazione del bucket.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TablePagination
        component="div"
        count={totalEntries}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[15, 25, 50]}
        labelRowsPerPage="File per pagina:"
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={indeterminate}
                  checked={allChecked}
                  onChange={(e) => onToggleSelectAll(e.target.checked, entries)}
                />
              </TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Dimensione</TableCell>
            </TableRow>
          </TableHead>
            <TableBody>
            {entries.map((entry) => {
              if (entry.type === 'folder') {
                const folder = entry.folder;
                return (
                  <TableRow
                    key={folder.path}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onOpenFolder(folder.path)}
                  >
                    <TableCell padding="checkbox" />
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FolderIcon size={16} />
                        <Typography variant="body2" noWrap>
                          {folder.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        Cartella
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {folder.dateLabel ||
                          (folder.lastModified
                            ? new Date(folder.lastModified).toLocaleDateString('it-IT')
                            : '-')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 1
                        }}
                      >
                        <Typography variant="body2">
                          {folder.sizeLabel ||
                            (folder.size != null ? `${folder.size} B` : '-')}
                        </Typography>
                        <Tooltip title="Elimina cartella">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteFolder(folder);
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }

              const file = entry.file;
              const ext = (file.extension || '').toLowerCase();
              const isPdf = ext === 'pdf';
              const isEmail = ext === 'eml' || ext === 'msg';
              const isAudio = ext === 'mp3' || ext === 'wav';
              const color = isPdf
                ? '#D32F2F'
                : isEmail
                ? '#1565C0'
                : isAudio
                ? '#2E7D32'
                : '#455A64';
              const chipLabel = ext ? ext.toUpperCase() : file.mimeType || 'FILE';
              const checked = selectedIds.includes(file.key);
              let pathLabel = '';
              if (isDeepSearchActive) {
                const folderPath = file.folderPath || '';
                if (folderPath && currentPath && folderPath.startsWith(currentPath)) {
                  const relative = folderPath.slice(currentPath.length).replace(/^\/+/, '');
                  pathLabel = relative || 'Cartella corrente';
                } else if (folderPath) {
                  pathLabel = folderPath;
                }
              }
              return (
                <TableRow
                  key={file.key}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onOpenFile(file)}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelect(file.key);
                    }}
                  >
                    <Checkbox checked={checked} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: 1,
                          bgcolor: color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FileText size={12} color="#fff" />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" noWrap>
                          {file.name}
                        </Typography>
                        {isDeepSearchActive && pathLabel && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {pathLabel}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title="Elimina file">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteFile(file);
                          }}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={chipLabel}
                      sx={{
                        height: 22,
                        borderRadius: 999,
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {file.dateLabel ||
                        (file.lastModified
                          ? new Date(file.lastModified).toLocaleDateString('it-IT')
                          : '-')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {file.sizeLabel || (file.size != null ? `${file.size} B` : '-')}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function DocumentPreviewPanel({
  file,
  previewUrl,
  loading,
  onClose,
  onDownload,
  downloadLoading,
  onAnalyze,
  analyzeLoading
}) {
  if (!file) {
    return (
      <Box
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="body2" color="text.secondary" align="center">
          Seleziona un file per visualizzarne l\'anteprima.
        </Typography>
      </Box>
    );
  }

  const ext = (file.extension || '').toLowerCase();
  const isImage = file.mimeType && file.mimeType.startsWith('image/');
  const isPdf = ext === 'pdf' || (file.mimeType || '').includes('pdf');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1 }} noWrap>
          {file.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Apri documento in nuova scheda">
            <span>
              <IconButton
                size="small"
                aria-label="Apri documento"
                onClick={onDownload}
                disabled={downloadLoading}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                  boxShadow:
                    '0 0 2px 0 rgba(15, 23, 42, 0.2), 0 10px 20px -4px rgba(15, 23, 42, 0.3)'
                }}
              >
                <ExternalLink size={16} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Scarica documento">
            <span>
              <IconButton
                size="small"
                aria-label="Scarica documento"
                onClick={onDownload}
                disabled={downloadLoading}
                sx={{
                  borderRadius: 999,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  color: 'text.primary',
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'rgba(148, 163, 184, 0.08)'
                  }
                }}
              >
                <DownloadIcon size={16} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Analizza il contenuto del file con AI">
            <span>
              <IconButton
                size="small"
                aria-label="Analizza con AI"
                onClick={onAnalyze}
                disabled={analyzeLoading}
                sx={{
                  borderRadius: 999,
                  bgcolor: '#111827',
                  color: '#F9FAFB',
                  '&:hover': { bgcolor: '#020617' },
                  boxShadow:
                    '0 0 2px 0 rgba(15, 23, 42, 0.2), 0 10px 20px -4px rgba(15, 23, 42, 0.4)'
                }}
              >
                <Sparkles size={16} />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" variant="text" onClick={onClose}>
            Chiudi
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          p: 2
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            mb: 2
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && previewUrl && isPdf && (
            <Box
              sx={{
                height: { xs: '55vh', sm: '60vh' },
                maxHeight: '70vh'
              }}
            >
              <iframe
                title={file.name}
                src={previewUrl}
                style={{ border: 0, width: '100%', height: '100%' }}
              />
            </Box>
          )}
          {!loading && previewUrl && isImage && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={previewUrl}
                alt={file.name}
                style={{ maxWidth: '100%', maxHeight: 480, borderRadius: 8 }}
              />
            </Box>
          )}
          {!loading && (!previewUrl || (!isPdf && !isImage)) && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Anteprima non disponibile per questo tipo di file.
              </Typography>
            </Box>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Dettagli
          </Typography>
          <Typography variant="body2">Nome: {file.name}</Typography>
          <Typography variant="body2">
            Tipo: {(file.extension || '').toUpperCase() || file.mimeType || '-'}
          </Typography>
          <Typography variant="body2">
            Data:{' '}
            {file.dateLabel ||
              (file.lastModified
                ? new Date(file.lastModified).toLocaleDateString('it-IT')
                : '-')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Dimensione: {file.sizeLabel || (file.size != null ? `${file.size} B` : '-')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default DocumentManagerPage;
